import { designTokens } from "@amplecare/design-tokens";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";

import { styles } from "./AppShell.styles.js";
import { LineIcon, type LineIconName } from "./line-icons.js";
import type { AppShellTheme } from "./shell-core.js";

type WorkbenchActionTone =
  | "accent"
  | "success"
  | "danger"
  | "warning"
  | "info";

export function createWebOrderStyle(order: number): ViewStyle {
  return { order } as unknown as ViewStyle;
}

export function createWebViewportMinHeightStyle(): ViewStyle {
  return { minHeight: "100vh" } as unknown as ViewStyle;
}

export function createWebGradientBackgroundStyle(gradient: string): ViewStyle | null {
  if (Platform.OS !== "web") {
    return null;
  }

  return { backgroundImage: gradient } as unknown as ViewStyle;
}

export function renderWorkspaceFilterChip(input: {
  chipKey?: string;
  isSelected: boolean;
  labelText: string;
  onPress: () => void;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const { chipKey, isSelected, labelText, onPress, theme, tokens } = input;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      key={chipKey ?? labelText}
      onPress={onPress}
      style={(state) => {
        const stateRecord = state as unknown as Record<string, unknown>;
        const focused = stateRecord.focused === true;
        const hovered = stateRecord.hovered === true;
        const pressed = state.pressed === true;

        return [
          styles.inlinePill,
          {
            alignItems: "center",
            backgroundColor: pressed
              ? theme.backgroundSubtle
              : hovered || focused
                ? theme.backgroundSubtle
                : isSelected
                  ? theme.backgroundSubtle
                  : theme.surface,
            borderColor:
              isSelected || focused
                ? theme.accentStrong
                : hovered
                  ? theme.accent
                  : theme.borderSubtle,
            borderRadius: tokens.semanticLayout.controlRadius,
            justifyContent: "center",
            minHeight: 36,
            opacity: pressed ? 0.88 : 1,
            paddingHorizontal: tokens.spacing.md,
            paddingVertical: tokens.spacing.xs,
            shadowColor: theme.accentStrong,
            shadowOffset: { height: hovered || focused ? 2 : 0, width: 0 },
            shadowOpacity: hovered || focused ? 0.05 : 0,
            shadowRadius: hovered || focused ? 6 : 0,
          },
        ];
      }}
    >
      <Text
        numberOfLines={1}
        style={[
          styles.pillValueText,
          {
            color: isSelected ? theme.accentStrong : theme.textSecondary,
            textAlign: "center",
          },
        ]}
      >
        {labelText}
      </Text>
    </Pressable>
  );
}

export function renderLegacySurfaceHeader(input: {
  eyebrowText?: string | null;
  title: string;
  subtitle?: string | null;
  layout: typeof designTokens.semanticLayout;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    eyebrowText = null,
    title,
    subtitle = null,
    layout,
    theme,
    tokens,
  } = input;

  return (
    <View
      style={[
        styles.legacySurfaceHeader,
        {
          borderBottomColor: theme.borderStrong,
          gap: tokens.spacing.xs,
          marginBottom: tokens.spacing.xs,
          paddingBottom: tokens.spacing.sm,
        },
      ]}
    >
      {eyebrowText ? (
        <Text
          style={[
            styles.legacySurfaceEyebrow,
            {
              color: theme.accent,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
            },
          ]}
        >
          {eyebrowText}
        </Text>
      ) : null}

      <Text
        style={[
          styles.surfaceTitle,
          {
            color: theme.textPrimary,
            fontFamily: layout.displayFontFamily,
            fontSize: tokens.typography.fontSize.lg,
            lineHeight: tokens.typography.lineHeight.lg,
          },
        ]}
      >
        {title}
      </Text>

      {subtitle ? (
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
}

export function renderWorkbenchActionButton(input: {
  disabled: boolean;
  iconName?: LineIconName | null;
  label: string;
  labelLines?: 1 | 2;
  maxWidth?: ViewStyle["maxWidth"];
  minHeight?: number;
  minWidth?: number;
  onPress: () => void;
  size?: "default" | "compact";
  stretch?: boolean;
  theme: AppShellTheme;
  tone: WorkbenchActionTone;
  tokens: typeof designTokens;
  width?: number;
}) {
  const buttonSize = input.size ?? "default";
  const isCompact = buttonSize === "compact";
  const toneColor =
    input.tone === "success"
      ? input.theme.success
      : input.tone === "danger"
        ? input.theme.danger
        : input.tone === "warning"
          ? input.theme.textSecondary
          : input.theme.accent;
  const backgroundColor = input.disabled
    ? input.theme.surfaceMuted
    : isCompact
      ? input.theme.surface
      : toneColor;
  const defaultLeadIconName: LineIconName =
    input.tone === "success"
      ? "check"
      : input.tone === "danger"
        ? "x"
        : input.tone === "warning"
          ? "stop"
          : input.tone === "info"
            ? "info"
            : "chevron-right";
  const leadIconName =
    input.iconName === undefined ? defaultLeadIconName : input.iconName;
  const leadSize = isCompact ? 16 : 20;
  const horizontalPadding = isCompact ? 10 : 10;
  const verticalPadding = isCompact ? 4 : 5;
  const labelFontSize =
    isCompact
      ? 11
      : input.tokens.typography.fontSize.xs;
  const leadFontSize = 10;
  const leadGap =
    leadIconName == null
      ? 0
      : isCompact
      ? input.tokens.spacing.xs
      : input.tokens.spacing.sm;
  const defaultMinWidth = isCompact ? 124 : 142;
  const labelColor = input.disabled
    ? input.theme.textSecondary
    : isCompact
      ? toneColor
      : input.theme.accentContrast;
  const leadBackgroundColor = input.disabled
    ? input.theme.surfaceMuted
    : isCompact
      ? input.theme.surfaceMuted
      : input.theme.textPrimary;
  const leadBorderColor = input.disabled
    ? input.theme.borderSubtle
    : isCompact
      ? toneColor
      : input.theme.textPrimary;
  const leadTextColor = input.disabled
    ? input.theme.textSecondary
    : isCompact
      ? toneColor
      : input.theme.surface;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: input.disabled }}
      disabled={input.disabled}
      onPress={input.onPress}
      style={(state) => {
        const stateRecord = state as unknown as Record<string, unknown>;
        const focused = stateRecord.focused === true;
        const hovered = stateRecord.hovered === true;
        const pressed = state.pressed === true;

        return [
          styles.inlinePill,
          {
            alignItems: "center",
            alignSelf: input.stretch ? "stretch" : "flex-start",
            backgroundColor:
              input.disabled
                ? input.theme.surfaceMuted
                : pressed || (isCompact && (hovered || focused))
                  ? input.theme.backgroundSubtle
                  : backgroundColor,
            borderColor: input.disabled
              ? input.theme.borderSubtle
              : focused || hovered
                ? input.theme.accentStrong
                : toneColor,
            borderRadius: input.tokens.semanticLayout.controlRadius,
            justifyContent: "center",
            maxWidth: input.maxWidth ?? "100%",
            minHeight: input.minHeight ?? (isCompact ? 36 : 40),
            minWidth: input.minWidth ?? defaultMinWidth,
            opacity: input.disabled ? 0.6 : 1,
            paddingLeft:
              leadIconName == null ? horizontalPadding : input.tokens.spacing.xs,
            paddingRight: horizontalPadding,
            paddingVertical: verticalPadding,
            shadowColor: input.theme.accentStrong,
            shadowOffset: { height: hovered || focused ? 2 : 0, width: 0 },
            shadowOpacity: hovered || focused ? 0.06 : 0,
            shadowRadius: hovered || focused ? 6 : 0,
            width: input.stretch ? undefined : input.width,
          },
        ];
      }}
    >
      <View
        style={[
          styles.actionButtonContent,
          {
            flexShrink: 1,
            gap: leadGap,
            minWidth: 0,
          },
        ]}
      >
        {leadIconName == null ? null : (
          <View
            style={[
              styles.actionButtonLead,
              {
                backgroundColor: leadBackgroundColor,
                borderColor: leadBorderColor,
                height: leadSize,
                width: leadSize,
              },
            ]}
          >
            <LineIcon
              color={leadTextColor}
              name={leadIconName}
              size={leadFontSize + 6}
              strokeWidth={2.4}
            />
          </View>
        )}

        <Text
          ellipsizeMode="tail"
          numberOfLines={input.labelLines ?? 2}
          style={[
            styles.badgeText,
            {
              color: labelColor,
              flexShrink: 1,
              fontFamily: designTokens.semanticLayout.bodyFontFamily,
              fontSize: labelFontSize,
              lineHeight: isCompact ? 14 : 16,
              minWidth: 0,
              textAlign: "center",
            },
          ]}
        >
          {input.label}
        </Text>
      </View>
    </Pressable>
  );
}
